/**
 * useEntitlementsStore — permissions, entitlements, subscription state, and
 * the granular paid-capability access set.
 *
 * Part of the useAuthStore split (architecture review item 4). Owns
 * authorisation-shaped data: what the user is allowed to do and what they're
 * paying for. Profile fields live in `useUserStore`; session/JWT state lives
 * in `useSessionStore`.
 *
 * Two layers of authorisation data live here, unified as of the
 * feature/entitlements merge:
 *
 *   1. **Coarse / subscription-shaped** (`activeEntitlements`, `permissions`,
 *      `subscription*`) — hydrated from the current-user payload via
 *      `setEntitlementData`. Drives subscription UI + the legacy
 *      `hasActiveEntitlements` / `hasPermission` getters.
 *   2. **Granular access set** (`allowed`, `denied`, `accessVersion`) — the
 *      canonical 3-segment capability keys (`tool.*.use`, `dataset.*.read`, …)
 *      resolved server-side by profiteia-backend (auth/access/resolver.py)
 *      and fetched from `/api/v2/me/access`. Drives the `can` / `canAny` /
 *      `canAll` getters and the `useAccess` composable. Before the first
 *      fetch resolves, the getters fall back to a plain lookup in the JWT
 *      claim list (`activeEntitlements`), which carries the same canonical
 *      keys.
 *
 * Refresh triggers for the granular set (handled by `useAccess`, the axios
 * interceptor in `clients/Client.js`, and the auth flow):
 *   - on login / successful token refresh
 *   - on `X-Entitlements-Version` mismatch in any authenticated response
 *   - on 403 `entitlement_missing`
 *   - on app focus if the cached set is older than 60s
 *
 * Feature-flag kill switches (deploy-time on/off) continue to live in
 * `utils/featureFlags.js` / `useFeatureFlags` — never let a feature flag grant
 * paid access, and never let an entitlement bypass a kill switch.
 */

import { defineStore } from 'pinia';
import { useUserStore } from './useUserStore';
import AccessRepository from '@/repositories/AccessRepository';
import { redirectToLoginOnSessionExpired } from '@/repositories/clients/sharedTokenRefresh';
import {
  asEntitlementArray,
  claimsSatisfyAllKeys,
  claimsSatisfyAnyKey,
  hasAccessToAllKeys,
  hasAccessToAnyKey,
} from '@/config/entitlementKeys';

const STALE_AFTER_MS = 60_000;
const CACHED_USER_KEY = 'cachedCurrentUser';

// Coalesce concurrent fetchAccess() calls into one in-flight request so the
// interceptor, useAccess on mount, and the 403 handler can all trigger a
// refresh in the same tick without firing parallel network requests.
let accessInFlight = null;

function isUnauthorized(error) {
  return error?.response?.status === 401;
}

function readAccessToken() {
  try {
    return window.sessionStorage.getItem('accessToken');
  } catch {
    // Hardened/private contexts can throw. Returning null keeps the
    // same-token comparison below conservative (it compares equal), so
    // teardown behaviour is unchanged where storage is unreadable.
    return null;
  }
}

function clearSessionStorage() {
  try {
    window.sessionStorage.removeItem('accessToken');
    window.sessionStorage.removeItem('isUserLoaded');
    window.localStorage.removeItem('refreshToken');
    window.localStorage.removeItem(CACHED_USER_KEY);
  } catch {
    // Storage can throw in hardened/private browser contexts. The redirect
    // still gets the user out of the invalid session.
  }
}

export const useEntitlementsStore = defineStore('entitlements', {
  state: () => ({
    // Coarse / subscription-shaped layer.
    permissions: [],
    isSuperUser: false,
    activeEntitlements: [],
    isSubscribed: false,
    hasOrHadSubscription: false,
    subscription: false,
    subscriptionPlan: null,
    hasValidSubscription: false,
    validSubscriptions: [],
    showPaymentPopup: false,
    billingAccountState: null,
    trialUsed: false,
    trialEligible: false,
    trialExpired: false,
    trialEndsAt: null,
    // `base` | `pro` while a public-funnel Checkout was started but never paid.
    pendingCheckoutPlan: null,

    // Granular access set (from /api/v2/me/access).
    allowed: [],
    denied: [],
    unknownKeys: [],
    accessVersion: null,
    accessLoadedAt: 0,
    accessLoading: false,
    accessError: null,
  }),

  getters: {
    hasActiveEntitlements() {
      return (expectedEntitlement) => {
        if (this.activeEntitlements && this.activeEntitlements.length > 0) {
          return expectedEntitlement.every((e) => this.activeEntitlements.includes(e));
        }
        return false;
      };
    },
    hasPermission() {
      return (expectedPermission) => {
        const userStore = useUserStore();
        // Fail CLOSED: until the user record hydrates we have no permissions
        // to check against, so deny. (Was `return true`, which let every
        // gated-UI check pass before hydration.) The backend still enforces
        // the real authorization regardless.
        if (!userStore.id) return false;
        return this.permissions.includes(expectedPermission);
      };
    },

    /**
     * Whether the principal holds a specific canonical entitlement key.
     *
     * Super-user bypasses all checks. Explicit denies override allows.
     * Until the first successful /api/v2/me/access fetch, falls back to a
     * claim-aware lookup in the JWT claim list (`activeEntitlements`) — the
     * claim carries UNEXPANDED plan keys (`dataset.*.read` umbrella,
     * `dataset.flex.read` group), so the fallback matches wildcards and
     * group claims rather than exact strings; callers that want to delay
     * rendering should watch `accessLoadedAt`.
     *
     * @returns {(key: string) => boolean}
     */
    can() {
      return (key) => {
        if (this.isSuperUser) return true;
        if (hasAccessToAnyKey(this.denied, key)) return false;
        if (hasAccessToAnyKey(this.allowed, key, this.denied)) return true;
        if (!this.accessLoadedAt) {
          return claimsSatisfyAnyKey(this.activeEntitlements, key, this.denied);
        }
        return false;
      };
    },
    /**
     * @returns {(keys: string[]) => boolean}
     */
    canAny() {
      return (keys) => {
        if (this.isSuperUser) return true;
        const required = asEntitlementArray(keys);
        if (required.length === 0) return false;
        if (hasAccessToAnyKey(this.allowed, required, this.denied)) return true;
        if (!this.accessLoadedAt) {
          return claimsSatisfyAnyKey(this.activeEntitlements, required, this.denied);
        }
        return false;
      };
    },
    /**
     * @returns {(keys: string[]) => boolean}
     */
    canAll() {
      return (keys) => {
        if (this.isSuperUser) return true;
        const required = asEntitlementArray(keys);
        if (required.length === 0) return false;
        if (hasAccessToAllKeys(this.allowed, required, this.denied)) return true;
        if (!this.accessLoadedAt) {
          return claimsSatisfyAllKeys(this.activeEntitlements, required, this.denied);
        }
        return false;
      };
    },
    /**
     * `full` (default) or `api` — the reduced API portal for tenants that only
     * buy dataset access. Read from the Scrumble user response
     * (`tenant.portal_mode`); absent means `full`. UX only: entitlements stay
     * the boundary.
     */
    portalMode() {
      return useUserStore().tenant?.portal_mode === 'api' ? 'api' : 'full';
    },
    isApiOnly() {
      return this.portalMode === 'api';
    },
    isAccessStale() {
      return this.accessLoadedAt === 0 || Date.now() - this.accessLoadedAt > STALE_AFTER_MS;
    },
  },

  actions: {
    setEntitlementData(payload) {
      this.permissions = payload.permissions ?? [];

      const validSubscriptions = Array.isArray(payload.valid_subscriptions)
        ? payload.valid_subscriptions
        : [];
      const hasValidSubscription =
        typeof payload.has_valid_subscription === 'boolean'
          ? payload.has_valid_subscription
          : validSubscriptions.length > 0 || Boolean(payload.subscription);

      this.isSubscribed = hasValidSubscription;
      this.subscription = hasValidSubscription
        ? validSubscriptions[0] ?? payload.subscription ?? null
        : null;
      if (!hasValidSubscription) this.subscriptionPlan = null;

      this.isSuperUser =
        payload.isSuperUser === true ||
        (payload.permissions ?? []).includes('create_admins');

      this.showPaymentPopup = payload.show_payment_popup ?? false;
      this.activeEntitlements = payload.active_entitlements ?? [];
      this.hasOrHadSubscription = payload.has_or_had_subscription ?? false;
      this.hasValidSubscription = hasValidSubscription;
      this.validSubscriptions = validSubscriptions;
      this.billingAccountState = payload.billing_account_state ?? null;
      this.trialUsed = payload.trial_used ?? false;
      this.trialEligible = payload.trial_eligible ?? false;
      this.trialExpired = payload.trial_expired ?? false;
      this.trialEndsAt = payload.trial_ends_at ?? null;
      this.pendingCheckoutPlan = payload.pending_checkout_plan ?? null;
    },

    /**
     * Apply a resolved access set from /api/v2/me/access.
     *
     * @param {{
     *   allowed?: string[],
     *   denied?: string[],
     *   is_super_user?: boolean,
     *   unknown_keys?: string[],
     *   version?: string|null,
     * }} payload
     */
    setAccessData(payload) {
      this.allowed = Array.isArray(payload?.allowed) ? payload.allowed : [];
      this.denied = Array.isArray(payload?.denied) ? payload.denied : [];
      this.unknownKeys = Array.isArray(payload?.unknown_keys) ? payload.unknown_keys : [];
      this.accessVersion = payload?.version ?? null;
      this.accessLoadedAt = Date.now();
      this.accessError = null;
      // The access endpoint is authoritative for the super-user flag too.
      if (typeof payload?.is_super_user === 'boolean') {
        this.isSuperUser = payload.is_super_user;
      }
    },

    /**
     * Fetch the access set from /api/v2/me/access. Concurrent calls are
     * coalesced into a single network request. On transient failure the
     * cached set is preserved (last-known-good).
     *
     * @returns {Promise<{version: string|null}|null>}
     */
    async fetchAccess() {
      if (!readAccessToken()) return null;
      if (accessInFlight) return accessInFlight;
      // The token this request rides on. A 401 only justifies tearing the
      // session down if that token is STILL the current one: `accessInFlight`
      // coalescing hands a later caller the promise of an EARLIER request, so
      // a boot request still unsettled when the user logs in would otherwise
      // return its 401 to the fresh session and log it straight back out.
      const tokenAtStart = readAccessToken();
      this.accessLoading = true;
      accessInFlight = AccessRepository.fetchAccess()
        .then(({ data }) => {
          this.setAccessData(data);
          this.accessLoading = false;
          return { version: data?.version ?? null };
        })
        .catch(async (error) => {
          this.accessLoading = false;
          if (isUnauthorized(error) && readAccessToken() === tokenAtStart) {
            clearSessionStorage();
            try {
              const { useSessionStore } = await import('./useSessionStore');
              const sessionStore = useSessionStore();
              sessionStore.setIsAuthenticated(false);
              sessionStore.setUserLoaded(false);
            } catch {
              // If the session store cannot be resolved during a partial app
              // boot, storage + user/entitlement cleanup below still expires
              // the invalid session.
            }
            const userStore = useUserStore();
            userStore.clearUserData();
            this.clearEntitlements();
            this.accessError = 'session_expired';
            redirectToLoginOnSessionExpired();
            return null;
          }
          this.accessError = error?.message || 'access_fetch_failed';
          return null;
        })
        .finally(() => {
          accessInFlight = null;
        });
      return accessInFlight;
    },

    /**
     * Refetch only when the server-reported version differs from the one we
     * already hold. Called by the response interceptor on every authenticated
     * response carrying a mismatching `X-Entitlements-Version` header.
     */
    syncAccessVersion(serverVersion) {
      if (!serverVersion) return undefined;
      if (this.accessVersion === serverVersion) return undefined;
      return this.fetchAccess();
    },

    /**
     * Fetch only if the cached set is stale (>60s) or never loaded. Used on
     * app focus and on lazy lookup from useAccess.
     */
    refreshAccessIfStale() {
      if (!this.isAccessStale) return undefined;
      return this.fetchAccess();
    },

    clearEntitlements() {
      this.permissions = [];
      this.isSuperUser = false;
      this.activeEntitlements = [];
      this.isSubscribed = false;
      this.hasOrHadSubscription = false;
      this.subscription = false;
      this.subscriptionPlan = null;
      this.hasValidSubscription = false;
      this.validSubscriptions = [];
      this.showPaymentPopup = false;
      this.billingAccountState = null;
      this.trialUsed = false;
      this.trialEligible = false;
      this.trialExpired = false;
      this.trialEndsAt = null;
      this.pendingCheckoutPlan = null;

      this.allowed = [];
      this.denied = [];
      this.unknownKeys = [];
      this.accessVersion = null;
      this.accessLoadedAt = 0;
      this.accessLoading = false;
      this.accessError = null;
    },
  },
});
